import { createFileRoute, Link } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { CtaLink } from "@/components/marketing/CtaLink";
import {
  ArrowRight, Check, Star, Leaf, Plus, Minus,
  CalendarCheck, FileSignature, Radar, Sparkles, Users, ListChecks, KanbanSquare, Video,
} from "lucide-react";
import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { BeforeAfterSlider } from "@/components/marketing/BeforeAfterSlider";
import { useCurrency } from "@/lib/ecom/currency";
import { formatMoney, getProductBasePrice } from "@/lib/ecom/catalog";
import { getPlanMonthly, formatPlanPrice, defaultPlanPriceText } from "@/components/marketing/pricing-data";
import prodMetal from "@/assets/products/custom-black-metal.webp";
import prodGold from "@/assets/products/custom-gold.webp";
import prodBamboo from "@/assets/products/custom-bamboo.webp";
import logoSvg from "@/assets/banana-logo.svg";
import heroCard from "@/assets/hero-card-new.webp";
import networking from "@/assets/networking-new.png";
import aiAbstract from "/lovable-uploads/11749302-d433-4877-9acb-ac3108b72948.webp";
import phoneCard from "/lovable-uploads/38b31f4c-5833-4861-9072-f959be0b21bb.webp";
import nfcTrio from "@/assets/nfc-card-trio-v2.png";
import nfcTap from "@/assets/nfc-card-tap-new.png";
import nfcLifestyle from "@/assets/nfc-card-lifestyle-new.png";
import nfcThin from "@/assets/nfc-thin.webp";
import floatCardScan from "@/assets/float-card-scan.webp";
import floatProfile from "@/assets/float-profile.webp";
import floatData from "@/assets/float-data.png";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Digital Business Cards & AI Networking Platform | Banana Cards" },
      { name: "description", content: "Banana Cards is the AI-powered networking platform that combines digital business cards, NFC sharing, lead capture, CRM, and smart contact management in one place." },
      { property: "og:title", content: "Digital Business Cards & AI Networking Platform | Banana Cards" },
      { property: "og:description", content: "Banana Cards is the AI-powered networking platform that combines digital business cards, NFC sharing, lead capture, CRM, and smart contact management in one place." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://banana.cards/" },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "vi_VN" },
    ],
    links: [
      ...alternateLinkList("/", "en"),
      { rel: "preload", as: "image", href: heroCard, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Banana Cards",
          url: "https://banana.cards/",
          logo: "https://banana.cards/favicon.ico",
          sameAs: [
            "https://app.banana.cards",
            "https://shop.banana.cards",
          ],
          description: "AI-powered networking platform combining NFC digital business cards, lead capture, CRM, bookings, contracts and smart contact enrichment.",
          contactPoint: [
            {
              "@type": "ContactPoint",
              contactType: "customer support",
              email: "hello@banana.cards",
              availableLanguage: ["English", "Vietnamese"],
              areaServed: "Worldwide",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Banana Cards",
          url: "https://banana.cards/",
          inLanguage: ["en", "vi"],
          publisher: { "@type": "Organization", name: "Banana Cards", url: "https://banana.cards/" },
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://banana.cards/help?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Banana Cards Platform",
          url: "https://banana.cards/platform",
          operatingSystem: "Web, iOS, Android",
          applicationCategory: "BusinessApplication",
          applicationSubCategory: "CRM",
          description: "AI-powered CRM combining NFC business cards, lead capture, contacts, pipelines, bookings, contracts, reviews and automations in one workspace.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            category: "Freemium",
          },
          featureList: [
            "NFC digital business cards",
            "Lead capture and contact enrichment",
            "Sales pipelines and tasks",
            "Bookings and meetings",
            "E-signature contracts",
            "Automated review requests",
            "AI drafting and voice assistant",
            "Apple Wallet and Google Wallet passes",
          ],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            reviewCount: "1280",
            bestRating: "5",
            worstRating: "1",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Banana NFC Business Cards",
          numberOfItems: 6,
          itemListElement: [
            { slug: "metal", name: "Custom Metal AI Business Card", price: 72 },
            { slug: "24kt-gold", name: "Custom 24kt Gold AI Business Card", price: 108 },
            { slug: "bamboo", name: "Bamboo Smart Business Card", price: 60 },
            { slug: "white-pvc", name: "Custom White PVC AI Business Card", price: 54 },
            { slug: "black-pvc", name: "Custom Black PVC AI Business Card", price: 54 },
            { slug: "google-review", name: "Google Review Cards", price: 30 },
          ].map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://banana.cards/shop/${p.slug}`,
            item: {
              "@type": "Product",
              name: p.name,
              url: `https://banana.cards/shop/${p.slug}`,
              brand: { "@type": "Brand", name: "Banana Cards" },
              category: "NFC Business Cards",
              offers: {
                "@type": "Offer",
                price: p.price,
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
                url: `https://banana.cards/shop/${p.slug}`,
              },
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "How does a Banana NFC card work?", acceptedAnswer: { "@type": "Answer", text: "Just tap your card against any modern smartphone — iPhone or Android. Your full digital profile opens instantly in the recipient's browser. No app required for either side." } },
            { "@type": "Question", name: "Do I need an app to use it?", acceptedAnswer: { "@type": "Answer", text: "You don't. The person receiving your card doesn't need anything installed. As the cardholder, you manage your profile from our web dashboard or our mobile companion app — your choice." } },
            { "@type": "Question", name: "Can I update my info after my card is printed?", acceptedAnswer: { "@type": "Answer", text: "Yes — that's the whole point. Your physical card links to your live digital profile. Change your job, number, links or design anytime and it updates everywhere instantly." } },
            { "@type": "Question", name: "Will it work with my phone?", acceptedAnswer: { "@type": "Answer", text: "Banana cards work with virtually every smartphone made in the last 7+ years. iPhone XR and later open with a tap, and any phone can scan the built-in QR code." } },
            { "@type": "Question", name: "Can I order cards for my whole team?", acceptedAnswer: { "@type": "Answer", text: "Absolutely. Our Teams plan lets you provision cards in bulk, control branding centrally, and manage every employee's profile from one admin dashboard." } },
            { "@type": "Question", name: "What's included in the free plan?", acceptedAnswer: { "@type": "Answer", text: "A full digital business card with QR + link sharing, Apple & Google Wallet support, basic analytics, and unlimited contact saves. No credit card required to start." } },
            { "@type": "Question", name: "Do you ship internationally?", acceptedAnswer: { "@type": "Answer", text: "Yes — we ship to 100+ countries with tracked delivery. Shipping cost and timing are shown at checkout based on your destination." } },
            { "@type": "Question", name: "What is your return policy?", acceptedAnswer: { "@type": "Answer", text: "If you're not happy with your card, contact us within 30 days for a free replacement or refund. Custom-printed cards are covered for manufacturing defects." } },
          ],
        }),
      },
    ],
  }),
  component: IndexPage,
});

import { TRUSTED_LOGOS } from "@/assets/logos";

const PRODUCTS = [
  {
    title: "NFC Business Cards",
    desc: "Tap any phone. Share your full digital profile instantly — no app needed for the recipient. Metal, matte or bamboo.",
    img: nfcTrio,
    bullets: ["Tap-to-share, no app required", "Metal, matte & bamboo finishes", "Custom logo & design"],
    href: "/shop",
    cta: "Shop NFC cards",
  },
  {
    title: "Virtual Cards",
    desc: "Your fully digital profile — share via QR code, link, or Apple/Google Wallet. Always up to date. Always with you.",
    img: phoneCard,
    bullets: ["QR, link, Apple & Google Wallet", "Update anytime, instantly", "No physical card needed"],
    href: "/platform/cards",
    cta: "Create your card",
  },
  {
    title: "Banana Apps",
    desc: "Bolt on bookings, contracts, reviews and AI tools to your profile. One platform, every workflow.",
    img: aiAbstract,
    bullets: ["Bookings, contracts, reviews", "AI follow-ups & enrichment", "50+ native integrations"],
    href: "/platform/bookings",
    cta: "Explore the platform",
  },
];

const CARD_VARIANTS: { slug: string; name: string; desc: string; img: string; href: string }[] = [
  { slug: "metal",     name: "Custom Metal AI Card",  desc: "Premium metal NFC card built to last — powered by AI.",        img: prodMetal,  href: "/shop/metal" },
  { slug: "24kt-gold", name: "24kt Gold AI Card",     desc: "Luxury 24kt gold plated NFC card — designed to impress.",       img: prodGold,   href: "/shop/24kt-gold" },
  { slug: "bamboo",    name: "Bamboo AI Card",        desc: "Sustainable bamboo NFC card — smart, natural, unforgettable.",  img: prodBamboo, href: "/shop/bamboo" },
];

const PLATFORM_FEATURES = [
  {
    eyebrow: "A stunning digital profile.",
    italic: "Always on.",
    desc: "Your card lives at a permanent link — fully branded, instantly shareable, and always showing your latest info. No app needed to view or save.",
    chips: ["QR Code", "Apple Wallet", "Google Wallet", "NFC Tap", "Link Share"],
    img: phoneCard,
    href: "/platform/cards",
    cta: "Digital Business Cards",
  },
  {
    eyebrow: "Never lose a contact",
    italic: "again.",
    desc: "Every person who scans your card is captured automatically. Add notes, tags, and follow-up reminders. See who viewed your card and when.",
    chips: ["Auto-capture", "Notes & Tags", "Follow-up", "Analytics"],
    img: networking,
    href: "/platform/contacts",
    cta: "Contacts",
  },
  {
    eyebrow: "Manage your team's cards",
    italic: "from one place.",
    desc: "Create cards for your whole team in minutes. Set brand guidelines, update contact info centrally, and track engagement across your organisation.",
    chips: ["Team Dashboard", "Bulk Provisioning", "Brand Control", "Admin"],
    img: heroCard,
    href: "/teams",
    cta: "Teams",
  },
  {
    eyebrow: "Connects to the tools",
    italic: "you already use.",
    desc: "Push new contacts directly to your CRM. Sync with HubSpot, Salesforce, Pipedrive, and more — no manual data entry.",
    chips: ["HubSpot", "Salesforce", "Outlook", "Zapier", "Pipedrive", "Slack"],
    img: aiAbstract,
    href: "/platform/integrations",
    cta: "Integrations",
  },
  {
    eyebrow: "Know exactly how your card",
    italic: "performs.",
    desc: "Track views, saves and link clicks in real time. Understand which contacts engage and optimise your networking ROI.",
    chips: ["Card Views", "Saves", "Link Clicks", "Team Analytics"],
    img: nfcLifestyle,
    href: "/platform/analytics",
    cta: "Analytics",
  },
];

const STEPS = [
  { tag: "Setup",   title: "Build your card",  desc: "Create your digital profile in minutes. Add your photo, links and branding. Customize your NFC card design if ordering physical." },
  { tag: "Share",   title: "Connect anywhere", desc: "Tap your NFC card, show your QR code, or send your link. Recipients see your full profile instantly — no app, no friction." },
  { tag: "Manage",  title: "Grow your network",desc: "Every connection is automatically saved and organised. Follow up smarter, sync to your CRM, and never let a lead go cold." },
];

const PERSONAS = [
  { title: "For leaders & founders",      desc: "Make every first impression count. Your card reflects the quality of your brand — premium, polished, always current.", img: heroCard,     href: "/solutions/founders", cta: "Explore founder solutions" },
  { title: "For sales & business dev",    desc: "Close more deals. Capture every lead. Sync contacts to your CRM automatically and follow up before the competition does.",     img: networking,   href: "/solutions/sales",    cta: "See sales features" },
  { title: "For creatives & freelancers", desc: "Stand out from the crowd. Your digital card is a mini portfolio — add your website, socials, video and work samples.",          img: phoneCard,    href: "/solutions/founders", cta: "See freelancer features" },
];

const SPECS = [
  { v: "0.8mm",       l: "thin",          d: "Thinner than a standard credit card" },
  { v: "NFC + QR",    l: "two-in-one",    d: "Two ways to share in one card" },
  { v: "Any phone",   l: "compatibility", d: "iPhone & Android, no app needed" },
  { v: "Custom",      l: "design",        d: "Full-bleed print, engraving or embossing" },
  { v: "Instant",     l: "updates",       d: "Change your digital profile anytime" },
  { v: "Durable",     l: "build",         d: "Waterproof, scratch-resistant" },
];

const STATS = [
  { v: "200k+", l: "cards delivered worldwide" },
  { v: "100+",  l: "countries shipped" },
  { v: "4.9",   l: "average rating" },
  { v: "50k+",  l: "active users" },
];

const REVIEWS = [
  { q: "banana.cards completely changed the way our sales team networks. Every lead is auto-captured and pushed to Salesforce. Zero manual work.", a: "James T.", r: "VP Sales, Finvi" },
  { q: "The NFC card is stunning — people always ask about it at events. Being able to update my info anytime without reprinting is a game-changer.", a: "Sarah M.", r: "Founder" },
  { q: "We rolled this out across 200 employees in one afternoon. The team dashboard makes it incredibly easy to manage everyone's cards centrally.", a: "Mark R.",  r: "IT Director" },
  { q: "I love that recipients don't need to download anything. They just tap or scan and boom — my contact is on their phone.",                       a: "Lucas P.", r: "Real Estate Agent" },
  { q: "Finally a business card as premium as our brand. The metal card with our logo engraved looks incredible.",                                     a: "Elena K.", r: "Creative Director" },
  { q: "The analytics are brilliant — I see exactly who viewed my card after each event and follow up while the conversation is fresh.",               a: "David W.", r: "Sales Executive" },
];

const REVIEWS_EXTRA = [
  { q: "As an Account Executive Manager, this tool helps me manage contacts for myself, my team and beyond. The card has significantly improved efficiency.", a: "Anna-Rose H.", r: "Account Executive Manager" },
  { q: "No more lost business cards from others and running out of cards others will lose. Love it!", a: "Allen P.", r: "Career Ownership Coach" },
  { q: "Save the trees and the tap feature is so cool for newer phones.", a: "Max R.", r: "Salesperson" },
  { q: "I really love the simplicity, choice of materials, and the sleek design. The app interface is enjoyable to use.", a: "Angelica A.", r: "Co-owner" },
  { q: "It is very easy to exchange info with anyone you meet. You don't need printed business cards. The info goes right into your contacts.", a: "Ozana G.", r: "Marketing Lead" },
  { q: "The ability to personalize colors, fonts, and layouts makes it a perfect reflection of your brand or style.", a: "Tatiana E.", r: "Travel Agent" },
  { q: "The design and simplicity of setting up the profile, and the convenience of tapping my card to share everything — incredible.", a: "Brandon R.", r: "Industrial Inspector" },
  { q: "I personally like the ease and security of digital card sharing. I don't need to carry additional business cards.", a: "Priya N.", r: "Consultant" },
  { q: "Sustainable, beautiful, and our whole leadership team uses them. Big upgrade from paper.", a: "Diego F.", r: "COO" },
];

const FAQS = [
  { q: "How does a Banana NFC card work?", a: "Just tap your card against any modern smartphone — iPhone or Android. Your full digital profile opens instantly in the recipient's browser. No app required for either side." },
  { q: "Do I need an app to use it?", a: "You don't. The person receiving your card doesn't need anything installed. As the cardholder, you manage your profile from our web dashboard or our mobile companion app — your choice." },
  { q: "Can I update my info after my card is printed?", a: "Yes — that's the whole point. Your physical card links to your live digital profile. Change your job, number, links or design anytime and it updates everywhere instantly." },
  { q: "Will it work with my phone?", a: "Banana cards work with virtually every smartphone made in the last 7+ years. iPhone XR and later open with a tap, and any phone can scan the built-in QR code." },
  { q: "Can I order cards for my whole team?", a: "Absolutely. Our Teams plan lets you provision cards in bulk, control branding centrally, and manage every employee's profile from one admin dashboard." },
  { q: "What's included in the free plan?", a: "A full digital business card with QR + link sharing, Apple & Google Wallet support, basic analytics, and unlimited contact saves. No credit card required to start." },
  { q: "Do you ship internationally?", a: "Yes — we ship to 100+ countries with tracked delivery. Shipping cost and timing are shown at checkout based on your destination." },
  { q: "What is your return policy?", a: "If you're not happy with your card, contact us within 30 days for a free replacement or refund. Custom-printed cards are covered for manufacturing defects." },
];

const BLOGS = [
  { title: "What Is a Digital Business Card? (Complete 2026 Guide)", excerpt: "Discover what digital business cards are, how they work, and why 88% of professionals prefer them over paper.", img: phoneCard, href: "/about" },
  { title: "8 Best Digital Business Cards of 2026 (Ranked & Reviewed)", excerpt: "We reviewed and tested the 8 best digital business card platforms — pricing, free plans, and features.", img: networking, href: "/about" },
  { title: "How to Create a Digital Business Card in 5 Minutes", excerpt: "Build a professional digital business card in 5 minutes. No app required, free Apple Wallet, works on any device.", img: nfcTrio, href: "/about" },
  { title: "NFC vs QR: Which Sharing Method Wins in 2026?", excerpt: "A side-by-side breakdown of the two most popular ways to share contact info — and why we use both.", img: nfcTap, href: "/about" },
  { title: "The Sustainability Case for Going Paperless", excerpt: "Paper business cards cost the planet 7 million trees a year. Here's how digital cards change that.", img: aiAbstract, href: "/about" },
  { title: "Designing Cards That Get Remembered", excerpt: "Color, material and typography choices that make your physical NFC card unforgettable at events.", img: nfcLifestyle, href: "/about" },
];

type HomePlan = {
  name: string;
  planKey: "free" | "pro_ai" | "teams";
  desc: string;
  cta: string;
  href: string;
  features: string[];
  featured?: boolean;
};

const PLANS: HomePlan[] = [
  {
    name: "Free",
    planKey: "free",
    desc: "Perfect for individuals getting started.",
    cta: "Get started free",
    href: "/signup",
    features: ["1 digital card", "QR & link sharing", "Apple & Google Wallet", "Basic analytics", "Unlimited contact saves"],
  },
  {
    name: "Pro AI",
    planKey: "pro_ai",
    desc: "For professionals who network seriously.",
    cta: "Start Pro free",
    href: "/signup",
    features: ["Everything in Free", "Custom card design", "Advanced analytics", "CRM integrations", "Follow-up reminders", "Priority support"],
    featured: true,
  },
  {
    name: "Teams",
    planKey: "teams",
    desc: "For teams of 5 or more.",
    cta: "Book a demo",
    href: "/contact",
    features: ["Everything in Pro", "Team dashboard & admin", "Bulk card creation", "Brand & permission controls", "SSO & enterprise integrations", "Dedicated account manager"],
  },
];

function IndexPage() {
  const { country } = useCurrency();
  const heroVideoRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroVideoRef,
    offset: ["start end", "end start"],
  });
  const heroScale = useTransform(heroScroll, [0, 0.45, 1], [0.94, 1.04, 1.08]);

  const supersectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: superScroll } = useScroll({
    target: supersectionRef,
    offset: ["start 85%", "center center"],
  });
  // Images fly outward as user scrolls; video grows. At progress 1 (midpoint
  // of section in viewport) images are fully out and video is full size.
  // Linger 10% longer before flying out — start outward motion at 0.1
  const floatLeftX = useTransform(superScroll, [0.1, 1], [0, -520]);
  const floatLeftY = useTransform(superScroll, [0.1, 1], [0, 140]);
  const floatLeftR = useTransform(superScroll, [0.1, 1], [-6, -18]);

  const floatRightX = useTransform(superScroll, [0.1, 1], [0, 520]);
  const floatRightY = useTransform(superScroll, [0.1, 1], [0, 120]);
  const floatRightR = useTransform(superScroll, [0.1, 1], [6, 18]);

  const floatBottomX = useTransform(superScroll, [0.1, 1], [0, -120]);
  const floatBottomY = useTransform(superScroll, [0.1, 1], [0, 360]);
  const floatBottomR = useTransform(superScroll, [0.1, 1], [-3, -14]);

  const floatOpacity = useTransform(superScroll, [0.1, 0.9, 1], [1, 0.2, 0]);
  const videoScale = useTransform(superScroll, [0, 1], [0.82, 1.02]);

  // Mute toggles for both videos
  const heroVideoEl = useRef<HTMLVideoElement>(null);
  const featuresVideoEl = useRef<HTMLVideoElement>(null);
  const [heroMuted, setHeroMuted] = useState(true);
  const [featuresMuted, setFeaturesMuted] = useState(true);
  const toggleMute = (
    el: React.RefObject<HTMLVideoElement | null>,
    muted: boolean,
    setMuted: (v: boolean) => void,
  ) => {
    const next = !muted;
    setMuted(next);
    if (el.current) {
      el.current.muted = next;
      if (!next) {
        el.current.play().catch(() => {});
      }
    }
  };

  return (
    <SiteLayout>
      {/* SUPERCHARGED — white section with floating images around a video */}
      <section className="relative overflow-hidden bg-background">
        <div className="mx-auto max-w-[1400px] px-4 py-20 md:px-8 md:py-36">
          <div className="mx-auto max-w-3xl text-left md:text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-foreground border border-foreground/10">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              NETWORKING SUPERAPP
            </span>
            <h2 className="mt-5 font-display text-3xl font-bold leading-[1.1] md:whitespace-nowrap md:text-5xl">
              Your business.{" "}
              <span className="relative inline-block align-baseline">
                <svg
                  className="sc-svg"
                  viewBox="0 0 740 100"
                  preserveAspectRatio="xMinYMid meet"
                  aria-hidden="true"
                  focusable="false"
                >
                  <defs>
                    <linearGradient id="sc-grad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#fbbf77" />
                      <stop offset="20%" stopColor="#f59e88" />
                      <stop offset="40%" stopColor="#f06c8a" />
                      <stop offset="55%" stopColor="#ec4899" />
                      <stop offset="72%" stopColor="#c563d6" />
                      <stop offset="88%" stopColor="#b057ee" />
                      <stop offset="100%" stopColor="#a855f7" />
                    </linearGradient>
                  </defs>
                  <text x="0" y="80" className="sc-base">Supercharged.</text>
                  <text x="0" y="80" className="sc-fill" fill="url(#sc-grad)">Supercharged.</text>
                  <text x="0" y="80" className="sc-trail" stroke="url(#sc-grad)">Supercharged.</text>
                </svg>
                <span className="sr-only">Supercharged.</span>
              </span>
            </h2>
            <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-muted-foreground md:mx-auto md:text-lg">
              banana.cards are smart, AI-powered tools designed to help your business grow. Share your
              digital profile with a tap and unlock an ecosystem that tracks leads, writes follow-ups,
              and automates your workflow — your brand, your data, your business, all connected.
            </p>
            <div className="mt-8 flex w-full md:w-auto md:justify-center">
              <a href="https://app.banana.cards" target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-dark px-7 py-3.5 text-sm font-bold text-background transition hover:opacity-90 md:inline-flex md:w-auto">
                Get started for free <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div ref={supersectionRef} className="relative mx-auto mt-12 max-w-5xl md:mt-20">
            {/* Floating card scan — top-left */}
            <motion.img
              src={floatCardScan}
              alt="Business card scanner"
              style={{ x: floatLeftX, y: floatLeftY, rotate: floatLeftR, opacity: floatOpacity }}
              className="pointer-events-none absolute -left-6 -top-16 z-20 hidden w-[230px] rounded-2xl md:block md:w-[300px] lg:-left-20 lg:w-[360px]"
              loading="lazy"
            />
            {/* Floating profile — right */}
            <motion.img
              src={floatProfile}
              alt="Digital business card profile"
              style={{ x: floatRightX, y: floatRightY, rotate: floatRightR, opacity: floatOpacity }}
              className="pointer-events-none absolute -right-4 top-12 z-20 hidden w-[180px] rounded-3xl md:block md:w-[220px] lg:-right-16 lg:w-[260px]"
              loading="lazy"
            />
            {/* Floating data — bottom-left */}
            <motion.img
              src={floatData}
              alt="Analytics dashboard"
              style={{ x: floatBottomX, y: floatBottomY, rotate: floatBottomR, opacity: floatOpacity }}
              className="pointer-events-none absolute -bottom-12 left-2 z-20 hidden w-[200px] rounded-2xl md:block md:w-[240px] lg:-left-12 lg:-bottom-20 lg:w-[280px]"
              loading="lazy"
            />

            {/* Video — grows as user scrolls */}
            <motion.div
              style={{ scale: videoScale }}
              className="relative origin-center overflow-hidden rounded-2xl bg-brand-dark"
            >
              <video ref={featuresVideoEl} className="aspect-video w-full object-cover rounded-none opacity-100" autoPlay loop muted playsInline poster={heroCard} preload="none" {...({ fetchpriority: "high" } as Record<string, string>)}>
                <source src="/features-video.webm" type="video/webm" />
                <track kind="captions" />
              </video>
              <button
                type="button"
                onClick={() => toggleMute(featuresVideoEl, featuresMuted, setFeaturesMuted)}
                aria-label={featuresMuted ? "Unmute video" : "Mute video"}
                className="absolute bottom-4 right-4 z-30 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition hover:bg-black/75 md:bottom-5 md:right-5"
              >
                {featuresMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LOGO MARQUEE — Trusted by professionals at */}
      <section className="bg-white">
        <div className="relative pb-20 pt-8 md:pb-28 md:pt-10 border-[#fcfcfc]">
          <p className="mb-5 text-center text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground md:mb-6">
            Trusted by professionals at
          </p>
          <div className="relative overflow-hidden">
            <div className="animate-marquee flex w-max items-center gap-6 px-6 [mask-image:linear-gradient(to_right,transparent,#000_10%,#000_90%,transparent)] md:gap-10">
              {[...TRUSTED_LOGOS, ...TRUSTED_LOGOS].map((logo, i) => (
                <span
                  key={i}
                  className="inline-flex h-20 w-20 shrink-0 items-center justify-center md:h-24 md:w-24"
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    loading="lazy"
                    decoding="async"
                    width={96}
                    height={96}
                    className="h-full w-full object-contain"
                  />
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* APPS SHOWCASE — bento grid of platform apps */}
      <section className="relative overflow-hidden bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <div className="mb-12 flex flex-col items-start gap-6 md:mb-16 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-card px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-foreground">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
                AI POWERED CRM
              </span>
              <h2 className="mt-5 font-display text-3xl leading-[1.05] tracking-[-0.01em] md:text-5xl">
                Apps that <em className="italic">run your business.</em>
              </h2>
              <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-muted-foreground md:text-lg">
                Every banana.cards account comes with a growing suite of native apps —
                install only what you need, all connected to your contacts and AI.
              </p>
            </div>
            <Link
              to="/platform"
              className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background transition hover:opacity-90"
            >
              Browse all apps <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: CalendarCheck, name: "Bookings", tag: "Scheduling", desc: "Let leads book meetings the moment they tap your card. Calendar sync, reminders, payments — all built in.", href: "/platform/bookings", color: "from-[#E8F5D8] to-[#C9E89A]" },
              { icon: FileSignature, name: "Contracts", tag: "E-sign", desc: "Send, sign and store contracts without leaving the app. Legally binding, audit-ready.", href: "/platform/contracts", color: "from-[#FFE9D6] to-[#FFC79A]" },
              { icon: Video, name: "Meetings", tag: "Live calls", desc: "Smart video meetings with AI notes, action items and CRM sync built in.", href: "/platform/meetings", color: "from-[#D0EEFF] to-[#8FCEFF]" },
              { icon: Users, name: "CRM", tag: "Contacts", desc: "Every tap, scan and lead lands here — tagged, scored and ready to action.", href: "/platform/crm", color: "from-[#D6F3EE] to-[#9CDCD0]" },
              { icon: KanbanSquare, name: "Pipelines", tag: "Sales ops", desc: "Visual deal pipelines that move automatically as your contacts engage.", href: "/platform/pipelines", color: "from-[#E4E4E7] to-[#B8B8C0]" },
              { icon: ListChecks, name: "Tasks", tag: "Workflow", desc: "Turn conversations into tasks. Assign, track and close — solo or as a team.", href: "/platform/tasks", color: "from-[#FFF4C2] to-[#FFE066]" },
              { icon: Radar, name: "Scout", tag: "AI prospecting", desc: "Let AI find lookalikes of your best clients and start the outreach for you.", href: "/platform/scout", color: "from-[#DCE7FF] to-[#A8C0FF]" },
              { icon: Sparkles, name: "AI Suite", tag: "AI assistants", desc: "Follow-ups, summaries, enrichment and replies — written for you in your voice.", href: "/platform/ai-suite", color: "from-[#EADFFF] to-[#C7AEFF]" },
            ].map((app, i) => {
              const Icon = app.icon;
              const featured = false;
              return (
                <Link
                  key={app.name}
                  to={app.href}
                  className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl bg-card p-6 transition hover:-translate-y-1 ${
                    featured ? "lg:col-span-2 lg:row-span-1" : ""
                  }`}
                >
                  {/* Placeholder image area with gradient */}
                  <div className={`relative mb-5 flex h-32 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br ${app.color}`}>
                    <Icon className="h-12 w-12 text-foreground/70 transition group-hover:scale-110" strokeWidth={1.5} />
                    <span className="absolute left-3 top-3 inline-flex items-center rounded-full bg-background/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-foreground backdrop-blur">
                      {app.tag}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-display text-xl leading-tight md:text-2xl">{app.name}</h3>
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted transition group-hover:bg-foreground group-hover:text-background">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{app.desc}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[12px] font-medium uppercase tracking-[0.2em] text-muted-foreground md:mt-14">
            <span>+ Integrations</span><span className="text-foreground/20">·</span>
            <span>+ Analytics</span><span className="text-foreground/20">·</span>
            <span>+ Wallet</span><span className="text-foreground/20">·</span>
            <span>+ Lead Exchange</span><span className="text-foreground/20">·</span>
            <span>new apps every month</span>
          </div>
        </div>
      </section>

      {/* PERSONAS */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="mb-10 max-w-2xl md:mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Built for everyone</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            Whoever you are. <em className="italic">However you work.</em>
          </h2>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {PERSONAS.map((p) => (
            <Link key={p.title} to={p.href} className="group flex flex-col overflow-hidden rounded-2xl bg-card transition">
              <div className="aspect-[5/4] overflow-hidden bg-muted">
                <img src={p.img} alt={p.title} loading="lazy" width={1200} height={960} className="h-full w-full object-cover transition group-hover:scale-[1.02]" />
              </div>
              <div className="p-6 md:p-7">
                <h3 className="font-display text-xl font-bold leading-tight md:text-2xl">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold underline-offset-4 group-hover:underline">
                  {p.cta} <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-10 rounded-3xl bg-card p-7 md:mt-12 md:p-14">
          <div className="grid items-center gap-8 md:grid-cols-3">
            <div className="md:col-span-2">
              <h3 className="font-display text-2xl leading-tight md:text-4xl">
                Designed for teams.
              </h3>
              <p className="mt-3 max-w-xl text-sm text-muted-foreground md:text-base">
                From 5 to 5,000 employees — manage every card, brand and connection from one admin dashboard. Built for enterprise.
              </p>
            </div>
            <div className="md:text-right">
              <Link to="/teams" className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Explore team plans <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* PLATFORM — left sticky intro, right scrolling features */}
      <section className="relative -mt-8 rounded-t-[2rem] bg-muted md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-16 md:px-6 md:pb-28 md:pt-24">
          <div className="grid gap-10 md:grid-cols-12 md:gap-12">
            {/* Sticky intro — left column on desktop */}
            <aside className="md:col-span-5 md:sticky md:top-24 self-start">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Platform</p>
              <h2 className="mt-3 font-display text-3xl leading-[1.05] md:text-5xl">
                Your business. <em className="italic">Supercharged.</em>
              </h2>
              <p className="mt-5 max-w-md text-[15px] text-muted-foreground md:text-base">
                banana.cards is more than a card — it's your complete digital identity and networking hub.
                Built for individuals, teams and enterprises.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <CtaLink to="/signup" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-dark px-5 py-3 text-sm font-bold text-background transition hover:opacity-90">
                  Get started free <ArrowRight className="h-4 w-4" />
                </CtaLink>
                <Link to="/pricing" className="inline-flex items-center justify-center gap-2 rounded-xl bg-card px-5 py-3 text-sm font-bold transition hover:opacity-80">
                  See pricing
                </Link>
              </div>
              {/* Decorative count chip */}
              <p className="mt-10 hidden text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground md:block">
                <span className="font-display text-2xl text-foreground">{PLATFORM_FEATURES.length}</span>
                <span className="ml-2 align-middle">capabilities · one platform</span>
              </p>
            </aside>

            {/* Feature list — right column */}
            <div className="md:col-span-7">
              <ol className="space-y-5 md:space-y-6">
                {PLATFORM_FEATURES.map((f, i) => {
                  const total = PLATFORM_FEATURES.length;
                  const num = `0${i + 1}`;
                  return (
                    <li key={f.eyebrow} className="rounded-3xl bg-card p-6 md:p-8">
                      <div className="flex items-center justify-between gap-4">
                        <span className="inline-flex items-center gap-2 rounded-xl bg-foreground px-3 py-1.5 font-display text-sm font-bold tracking-wider text-background">
                          <span className="text-brand-lime">{num}</span>
                          <span className="text-background/60">/ 0{total}</span>
                        </span>
                        <Link to={f.href} className="inline-flex items-center gap-1 text-xs font-bold underline-offset-4 hover:underline">
                          {f.cta} <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                      <h3 className="mt-5 font-display text-2xl leading-[1.15] md:text-3xl">
                        {f.eyebrow} <em className="italic">{f.italic}</em>
                      </h3>
                      <p className="mt-3 max-w-xl text-[14px] leading-relaxed text-muted-foreground md:text-[15px]">
                        {f.desc}
                      </p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {f.chips.map((c) => (
                          <span key={c} className="inline-flex items-center rounded-full bg-muted px-3 py-1.5 text-[12px] font-semibold border">
                            {c}
                          </span>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* STATS — matches section above (muted), white box inside */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 pb-12 md:px-6 md:pb-16">
          <div className="grid grid-cols-2 gap-6 rounded-3xl bg-background p-8 md:grid-cols-4 md:gap-8 md:p-12">
            {STATS.map((s) => (
              <div key={s.l}>
                <p className="font-display text-4xl leading-none md:text-6xl">{s.v}</p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HERO — Change the way you connect */}
      <section className="relative -mt-8 overflow-hidden rounded-t-[2rem] bg-white md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-[1600px] px-4 pb-14 pt-10 md:px-8 md:pb-20 md:pt-24 2xl:px-12">
          <div className="mx-auto flex max-w-5xl flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-foreground border border-foreground/10">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              The future of networking
            </span>
            <h1 className="mt-5 font-display text-4xl leading-[1.05] tracking-[-0.02em] md:text-6xl md:leading-[1.02]">
              Change the way you connect.
            </h1>
            <p className="mt-5 max-w-2xl text-[15px] text-muted-foreground md:text-lg">
              More than a digital card. A complete networking platform — NFC, virtual cards, AI-powered
              contacts and team management. All in one.
            </p>
            <div className="mt-7 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
              <CtaLink to="/signup" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Get started free <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link to="/shop" className="inline-flex items-center justify-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/80">
                Order NFC cards
              </Link>
            </div>
            <p className="mt-5 text-[12px] text-muted-foreground">
              No credit card required · Works on iPhone & Android · Loved by 50,000+ professionals
            </p>
          </div>

          <motion.div ref={heroVideoRef} style={{ scale: heroScale }} className="relative mt-10 origin-center md:mt-20">
            <div className="relative">
              <div className="relative overflow-hidden rounded-2xl bg-brand-dark">
              <video
                ref={heroVideoEl}
                className="aspect-video w-full object-cover rounded-none opacity-100"
                autoPlay
                loop
                muted
                playsInline
                poster={heroCard}
                preload="metadata"
                // @ts-expect-error fetchpriority is valid HTML but missing from React types
                fetchpriority="high"
              >
                <source src="/hero-video.webm" type="video/webm" />
                <track kind="captions" />
              </video>
              <button
                type="button"
                onClick={() => toggleMute(heroVideoEl, heroMuted, setHeroMuted)}
                aria-label={heroMuted ? "Unmute video" : "Mute video"}
                className="absolute bottom-4 right-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition hover:bg-black/75 md:bottom-5 md:right-5"
              >
                {heroMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* CARD SPECS */}
      <section className="bg-muted">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:gap-12 md:px-6 md:py-24">
          <div className="relative">
            <div className="absolute -inset-6 rounded-[2rem] bg-foreground/5 blur-3xl" aria-hidden />
            <img src={nfcThin} alt="Macro view of the NFC chip" loading="lazy" width={1600} height={1280} className="relative w-full rounded-2xl object-cover" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Built to last</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Unbelievably thin. <em className="italic">Incredibly powerful.</em>
            </h2>
            <p className="mt-4 max-w-md text-[15px] text-muted-foreground md:text-base">
              Our NFC cards are thinner than a credit card, built to last, and embed a full digital identity with a single tap.
            </p>
            <dl className="mt-7 grid grid-cols-2 gap-x-5 gap-y-4 md:mt-8 md:gap-x-6 md:gap-y-5">
              {SPECS.map((s) => (
                <div key={s.l} className="rounded-xl bg-card p-4">
                  <dt className="font-display text-lg leading-none">{s.v}</dt>
                  <dd className="mt-2 text-xs leading-relaxed text-muted-foreground">{s.d}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>


      {/* PRODUCTS — One platform. Every way to connect. */}
      <section className="relative -mt-8 rounded-t-[2rem] bg-background md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-10 md:px-6 md:pb-24 md:pt-14">
        <div className="mb-10 flex flex-col items-start gap-4 md:mb-12 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Our products</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              One platform.<br />Every way to connect.
            </h2>
            <p className="mt-4 max-w-xl text-[15px] text-muted-foreground md:text-base">
              Whether you tap, scan, or share a link — banana.cards works everywhere you meet people.
            </p>
          </div>
          <Link to="/shop" className="inline-flex items-center gap-1 text-sm font-bold underline-offset-4 hover:underline">
            Order & customize online <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {PRODUCTS.map((p) => (
            <Link key={p.title} to={p.href} className="group flex flex-col overflow-hidden rounded-2xl bg-muted transition">
              <div className="aspect-[5/4] overflow-hidden bg-card">
                <img src={p.img} alt={p.title} loading="lazy" width={1200} height={960} className="h-full w-full object-cover transition group-hover:scale-[1.02]" />
              </div>
              <div className="flex flex-1 flex-col p-6 md:p-7">
                <h3 className="font-display text-xl leading-tight md:text-3xl">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <ul className="mt-5 grid gap-2 text-sm">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0" /><span className="font-medium">{b}</span></li>
                  ))}
                </ul>
                <span className="mt-7 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  {p.cta} <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        </div>
      </section>

      {/* CARD VARIANTS — Your business card. Your way. */}
      <section className="relative -mt-8 overflow-hidden rounded-t-[2rem] bg-black text-background md:-mt-10 md:rounded-t-[3rem]">
        <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="mb-10 flex flex-col items-start gap-4 md:mb-12 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-background/60">Order online</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
                Your business card. <em className="italic">Your way.</em>
              </h2>
              <p className="mt-4 max-w-xl text-[15px] text-background/70 md:text-base">
                Design, order and receive premium NFC and digital cards — fully customized to your brand.
              </p>
            </div>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {CARD_VARIANTS.map((c) => (
              <div key={c.name} className="group flex flex-col overflow-hidden rounded-2xl bg-white/[0.06] transition hover:bg-white/[0.07]">
                <div className="aspect-[5/4] overflow-hidden bg-muted">
                  <img src={c.img} alt={c.name} loading="lazy" width={1200} height={960} className="h-full w-full object-cover transition group-hover:scale-[1.02]" />
                </div>
                <div className="flex flex-1 flex-col p-6 md:p-7">
                  <h3 className="font-display text-xl font-bold leading-tight md:text-2xl">{c.name}</h3>
                  <p className="mt-2 text-sm text-background/65">{c.desc}</p>
                  <div className="mt-5 flex items-end justify-between gap-4 border-t border-white/10 pt-5">
                    <p className="font-display text-2xl leading-none">
                      From {formatMoney(getProductBasePrice(c.slug, country.code), country)}
                    </p>
                    <Link to={c.href} className="inline-flex items-center gap-1.5 rounded-xl bg-brand-lime px-4 py-2 text-xs font-bold text-brand-lime-foreground transition hover:opacity-90">
                      Customize <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/shop" className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-6 py-3 text-sm font-bold text-background transition hover:bg-white/5">
              Browse all products <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* STEPS — From zero to connected in 3 steps */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="mb-10 max-w-2xl md:mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Get started in seconds</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            From zero to connected <em className="italic">in 3 steps.</em>
          </h2>
        </div>
        <ol className="grid gap-5 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <li key={s.title} className="relative flex flex-col rounded-2xl bg-muted p-6 md:p-8">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-foreground font-display text-xl font-bold text-background md:h-16 md:w-16 md:text-2xl">
                0{i + 1}
              </span>
              <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{s.tag}</p>
              <h3 className="mt-2 font-display text-2xl leading-tight">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </li>
          ))}
        </ol>
        <div className="mt-10 flex justify-center">
          <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
            Start for free <ArrowRight className="h-4 w-4" />
          </CtaLink>
        </div>
      </section>

      {/* SUSTAINABILITY */}
      <section className="relative -mt-8 overflow-hidden rounded-t-[2rem] bg-black text-background md:-mt-10 md:rounded-t-[3rem]">
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:gap-12 md:px-6 md:py-24">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-background/60">Sustainability</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Good for you. <br /> <em className="italic">And for the planet.</em>
            </h2>
            <p className="mt-4 max-w-md text-[15px] text-background/70 md:text-base">
              Every banana.cards NFC card replaces hundreds of paper cards over its lifetime.
              Our cards are made from recycled or sustainably-sourced materials. Stop reprinting. Stop wasting.
            </p>
            <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 md:mt-10 md:gap-6 md:pt-8">
              {[
                { v: "500+", l: "paper cards saved per NFC card" },
                { v: "100%", l: "carbon-neutral shipping" },
                { v: "Eco",  l: "recycled PVC & bamboo options" },
              ].map((s) => (
                <div key={s.l}>
                  <dt className="font-display text-2xl leading-none md:text-4xl">{s.v}</dt>
                  <dd className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-background/55 md:text-[11px]">{s.l}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="relative">
            <BeforeAfterSlider
              beforeSrc="/lovable-uploads/six-before.webp"
              afterSrc="/lovable-uploads/six-after.webp"
              beforeAlt="Old paper business card"
              afterAlt="banana.cards NFC card with digital profile"
              beforeLabel="Paper"
              afterLabel="banana.cards"
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <ReviewsSection />

      {/* PRICING TEASER */}
      <section className="relative -mt-8 rounded-t-[2rem] bg-muted md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="mb-10 max-w-2xl md:mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Pricing</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Simple, transparent pricing. <em className="italic">Free to start.</em>
            </h2>
            <p className="mt-4 text-[15px] text-muted-foreground md:text-base">
              No hidden fees. No long-term commitment. Start free and scale as you grow.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {PLANS.map((p) => {
              const isFree = p.planKey === "free";
              const paidKey = p.planKey === "pro_ai" || p.planKey === "teams" ? p.planKey : null;
              const priceText = isFree
                ? `${country.symbol}0`
                : paidKey
                  ? formatPlanPrice(country, getPlanMonthly(paidKey, country.code, "month"))
                  : defaultPlanPriceText("pro_ai", "month");
              const perLabel = p.planKey === "teams" ? "/ seat / month" : "/ month";
              return (
              <div key={p.name} className={`relative flex flex-col rounded-2xl bg-card p-7 md:p-8 ${p.featured ? "ring-2 ring-foreground" : ""}`}>
                {p.featured && (
                  <span className="absolute -top-3 left-6 inline-flex h-6 items-center justify-center rounded-xl bg-brand-lime px-3 text-[11px] font-bold text-brand-lime-foreground">
                    Most popular
                  </span>
                )}
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{p.name}</p>
                <div className="mt-4 flex items-end gap-1.5">
                  <p className="font-display text-4xl leading-none md:text-5xl">{priceText}</p>
                  <p className="pb-1 text-sm text-muted-foreground">{perLabel}</p>
                </div>
                {!isFree && (
                  <span className="mt-1 inline-flex w-fit items-center rounded-full bg-brand-lime px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-brand-lime-foreground">
                    50% OFF Yearly
                  </span>
                )}
                <p className="mt-3 text-sm text-muted-foreground">{p.desc}</p>
                <CtaLink to={p.href} className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition ${p.featured ? "bg-brand-dark text-background hover:opacity-90" : "bg-muted hover:bg-muted/70"}`}>
                  {p.cta} <ArrowRight className="h-4 w-4" />
                </CtaLink>
                <ul className="mt-8 grid gap-2.5 border-t border-foreground/10 pt-6 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0" /><span className="font-medium">{f}</span></li>
                  ))}
                </ul>
              </div>
              );
            })}
          </div>
          <p className="mt-8 text-center text-xs text-muted-foreground">
            All plans include a 14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* BLOGS / RESOURCES */}
      <BlogsSection />

      {/* FINAL CTA — stacks onto the blogs section above */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="relative overflow-hidden rounded-3xl bg-brand-dark p-8 text-center text-background md:p-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_55%)]" aria-hidden />
          <img src={logoSvg} alt="" aria-hidden width={115} height={20} className="mx-auto h-5 w-auto brightness-0 invert" />
          <h2 className="mx-auto mt-5 max-w-3xl font-display text-3xl leading-[1.1] md:text-5xl">
            Start connecting <em className="italic text-brand-lime">differently.</em>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-[15px] text-background/70 md:text-base">
            Join 50,000+ professionals who've upgraded how they network. It takes 2 minutes to set up.
          </p>
          <div className="relative mt-7 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
            <CtaLink to="/signup" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]">
              Get started — free
            </CtaLink>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold text-background transition hover:bg-background/10">
              Talk to sales
            </Link>
          </div>
        </div>
        </div>
      </section>

    </SiteLayout>
  );
}

function ReviewCard({ q, a, r }: { q: string; a: string; r: string }) {
  return (
    <figure className="flex flex-col rounded-2xl bg-muted p-5 md:p-6">
      <div className="flex">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className="h-3.5 w-3.5 fill-foreground text-foreground" />
        ))}
      </div>
      <blockquote className="mt-3 text-[14px] leading-relaxed">"{q}"</blockquote>
      <figcaption className="mt-5 text-xs">
        <span className="font-bold">{a}</span> <span className="text-muted-foreground">— {r}</span>
      </figcaption>
    </figure>
  );
}

function ReviewsSection() {
  const all = [...REVIEWS, ...REVIEWS_EXTRA];
  const cols = [
    all.filter((_, i) => i % 3 === 0),
    all.filter((_, i) => i % 3 === 1),
    all.filter((_, i) => i % 3 === 2),
  ];
  const animClasses = ["animate-marquee-y", "animate-marquee-y-rev", "animate-marquee-y-slow"];
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
      <div className="mb-10 flex flex-col items-center gap-3 text-center md:mb-12">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">What people say</p>
        <h2 className="font-display text-3xl leading-[1.1] md:text-5xl">
          Loved by 50,000+ professionals <em className="italic">in 170 countries.</em>
        </h2>
        <div className="mt-2 flex items-center gap-2">
          <div className="flex">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-foreground text-foreground" />
            ))}
          </div>
          <p className="text-sm font-bold">4.9 / 5 · 1,200+ reviews</p>
        </div>
      </div>
      <div
        className="relative grid h-[640px] grid-cols-1 gap-4 overflow-hidden md:grid-cols-3 md:gap-5"
        style={{
          maskImage:
            "linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent)",
        }}
      >
        {cols.map((col, idx) => (
          <div key={idx} className={`flex flex-col gap-4 md:gap-5 ${animClasses[idx]}`}>
            {[...col, ...col].map((t, i) => (
              <ReviewCard key={`${idx}-${i}`} {...t} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-foreground/10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
        aria-expanded={open}
      >
        <span className="font-display text-lg md:text-xl">{q}</span>
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-card">
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      {open && <p className="pb-5 pr-12 text-[15px] leading-relaxed text-muted-foreground">{a}</p>}
    </div>
  );
}

function FAQSection() {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-5xl px-4 py-16 md:px-6 md:py-24">
      <div className="mb-10 text-center md:mb-14">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">FAQ</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
          Questions? <em className="italic">We've got answers.</em>
        </h2>
      </div>
      <div className="mx-auto max-w-3xl rounded-3xl bg-muted px-6 md:px-10">
        {FAQS.map((f) => (
          <FAQItem key={f.q} {...f} />
        ))}
      </div>
      </div>
    </section>
  );
}

function BlogsSection() {
  return (
    <section className="relative -mt-8 rounded-t-[2rem] bg-muted md:-mt-10 md:rounded-t-[3rem]">
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="mb-10 text-center md:mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Resources</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            Blogs, resources <em className="italic">and guides.</em>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] text-muted-foreground md:text-base">
            Quick answers to the most common questions about digital business cards and modern networking.
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {BLOGS.map((b) => (
            <Link key={b.title} to={b.href} className="group flex flex-col overflow-hidden rounded-2xl bg-card transition">
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img src={b.img} alt={b.title} loading="lazy" className="h-full w-full object-cover transition group-hover:scale-[1.03]" />
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-display text-lg leading-tight md:text-xl">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold underline-offset-4 group-hover:underline">
                  Read article <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
